import { ForwardedRef, forwardRef, Ref } from 'react'
import { FieldValues, Path } from 'react-hook-form'
import { TextInput as TextInputProps } from 'types/input'

/**
 * Common component for text input
 */
const TextInputWithRef = <T extends FieldValues>(
  {
    className = '',
    parentClassName = '',
    label,
    required,
    type = 'text',
    name = 'inputField' as Path<T>,
    register,
    error,
    hintText,
    placeholder,
    icon,
    iconPosition = 'start',
    disabled,
    ...props
  }: TextInputProps<T>,
  ref: Ref<HTMLInputElement>,
) => {
  const hookFormRegister = register ? { ...register(name) } : { ref: ref }

  return (
    <div className="relative w-full">
      {/* Label */}
      {label && (
        <label
          htmlFor={name}
          className="mb-1 text-xs font-semibold text-sidebar-primary"
        >
          {label}
          {required && <span className="text-error">*</span>}
        </label>
      )}
      {/* Input */}
      <div
        className={`flex w-full bg-white ${
          iconPosition === 'end' ? 'flex-row-reverse' : 'flex-row'
        } h-11 gap-2 rounded-lg border
       ${error ? 'border-error' : 'border-gray-300'}  px-3 focus:border-2 ${
         disabled ? 'cursor-not-allowed !bg-gray-200' : ''
       } ${parentClassName}`}
      >
        {icon && <div className="h-4 w-4 shrink-0 text-gray-400">{icon}</div>}
        <input
          id={name}
          type={type}
          placeholder={placeholder || label}
          disabled={disabled}
          className={`h-full w-full appearance-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-600 ${
            error ? 'border-secondary' : ''
          } text-sm text-primary focus:border-gray-500 focus:outline-none focus:ring-0 ${className}`}
          {...hookFormRegister}
          {...props}
        />
      </div>
      {/* error or Input text */}
      {(error || hintText) && (
        <div>
          {error ? (
            <p className="mt-1 text-xs text-error">{error}</p>
          ) : (
            <p className="mt-1 text-[11px] text-gray-500">{hintText}</p>
          )}
        </div>
      )}
    </div>
  )
}

const TextInput = forwardRef(TextInputWithRef) as <T extends FieldValues>(
  props: TextInputProps<T> & { ref?: ForwardedRef<HTMLInputElement> },
) => ReturnType<typeof TextInputWithRef>

export default TextInput
